import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, TextField, Chip, Button, Tabs, Tab, Checkbox, FormControlLabel, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InsightsIcon from '@mui/icons-material/Insights';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { uptimeAPI, serverResourcesAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';
import UptimeAverageDialog from '../components/UptimeAverageDialog';
import UptimeDetailDialog, { STATUS, MONTHS, HOURLY_NOTE, pct, mins } from '../components/UptimeDetailDialog';
import { saveBlob } from '../utils/download';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtUtc = (d) => (d ? new Date(d).toLocaleString([], { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC' : '-');
const ENVIRONMENTS = ['production', 'test', 'development', 'training', 'unknown'];
const PER = 25;
const thisMonth = () => { const n = new Date(); return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, '0')}`; };

function Monthly({ status }) {
  const [monthValue, setMonthValue] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [averages, setAverages] = useState(false);
  const [page, setPage] = useState(0);
  const [year, month] = monthValue.split('-').map(Number);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await uptimeAPI.summary(year, month)).data); }
    catch (err) { setData(null); setError(errText(err, 'Could not load uptime')); }
    finally { setLoading(false); }
  }, [year, month, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [filter, search, monthValue]);

  const customers = data?.customers || [];
  const counts = customers.reduce((c, x) => ({ ...c, [x.status]: (c[x.status] || 0) + 1 }), {});
  const shown = customers.filter((c) => (!filter || c.status === filter) && (!search.trim() || `${c.code} ${c.name}`.toLowerCase().includes(search.trim().toLowerCase())));

  const exportCsv = () => {
    const head = ['Customer code', 'Customer', 'Uptime %', 'Result', 'Counted downtime (min)', 'Maintenance taken off (min)', 'Downtime recorded (min)', 'Allowed (min)', 'Left (min)', 'Outages', 'Data coverage %'];
    const lines = shown.map((c) => [c.code, c.name, c.uptime_percent, (STATUS[c.status] || STATUS.no_data).label, c.counted_minutes, c.excluded_minutes, c.down_minutes, c.budget_minutes, c.budget_left_minutes, c.outages, c.coverage_percent]);
    const csv = [head, ...lines].map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    saveBlob(new Blob([csv], { type: 'text/csv' }), `customer-uptime-${monthValue}.csv`);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" type="month" label="Month" value={monthValue} onChange={(e) => e.target.value && setMonthValue(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Customer code or name" />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['missed', 'at_risk', 'on_track', 'met'].filter((k) => counts[k]).map((k) => (
            <Chip key={k} color={STATUS[k].color} variant={filter === k ? 'filled' : 'outlined'} label={`${STATUS[k].label}: ${counts[k]}`} onClick={() => setFilter(filter === k ? '' : k)} />
          ))}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<InsightsIcon />} onClick={() => setAverages(true)}>Average uptime</Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!shown.length}>Export CSV</Button>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {!loading && data && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {MONTHS[month - 1]} {year}{data.complete ? '' : ' (so far)'}. Allowed downtime at {data.target_percent}%: about {mins(customers[0]?.budget_minutes)} for the whole month.
          </Typography>
          {!data.complete && customers.length > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>For a month in progress the result compares the downtime so far with the whole month's allowance, so an early outage is not shown as missed until the allowance is used up. The uptime figure is over the time observed so far.</Typography>}
          {customers.some((c) => c.resolution_seconds > 300) && <Alert severity="warning" sx={{ mb: 2 }}>{HOURLY_NOTE}</Alert>}
          {customers.length === 0 ? <Alert severity="info">No uptime has been stored for this month yet.</Alert> : (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell><TableCell align="right">Uptime</TableCell><TableCell>Result</TableCell>
                      <TableCell align="right">Counted downtime</TableCell><TableCell align="right">Maintenance</TableCell><TableCell align="right">Left of allowance</TableCell>
                      <TableCell align="right">Outages</TableCell><TableCell>Now</TableCell><TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.slice(page * PER, page * PER + PER).map((c) => {
                      const st = STATUS[c.status] || STATUS.no_data;
                      return (
                        <TableRow key={c.customer_id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(c)}>
                          <TableCell><Typography variant="body2" fontWeight="bold">{c.code}</Typography><Typography variant="caption" color="text.secondary">{c.name}</Typography></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{pct(c.uptime_percent)}{c.resolution_seconds > 300 && <Tooltip title={HOURLY_NOTE}><Chip size="small" color="warning" label="Hourly detail" sx={{ ml: 1 }} /></Tooltip>}</TableCell>
                          <TableCell><Chip size="small" color={st.color} label={st.label} /></TableCell>
                          <TableCell align="right">{mins(c.counted_minutes)}</TableCell>
                          <TableCell align="right">{c.excluded_minutes > 0 ? mins(c.excluded_minutes) : '-'}</TableCell>
                          <TableCell align="right" sx={{ color: c.budget_left_minutes < 0 ? 'error.main' : undefined }}>{mins(c.budget_left_minutes)}</TableCell>
                          <TableCell align="right">{c.outages}</TableCell>
                          <TableCell>{c.now ? <Chip size="small" variant="outlined" color={c.now === 'up' ? 'success' : 'error'} label={c.now === 'up' ? 'Up' : 'Down'} /> : '-'}</TableCell>
                          <TableCell>{c.coverage_percent < 95 ? <Tooltip title="Probe results are missing for part of the month, so this figure may be incomplete"><Chip size="small" color="warning" label={`${c.coverage_percent}%`} /></Tooltip> : `${c.coverage_percent}%`}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={shown.length} page={page} rowsPerPage={PER} rowsPerPageOptions={[PER]} onPageChange={(e, p) => setPage(p)} />
            </Paper>
          )}
          {data.without_url.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {data.without_url.length} active customers have no production URL counted towards uptime yet ({data.without_url.map((c) => c.code).join(', ')}). An administrator can choose one on the Targets tab.
            </Alert>
          )}
        </>
      )}
      {open && <UptimeDetailDialog customer={open} year={year} month={month} onClose={() => setOpen(null)} />}
      {averages && <UptimeAverageDialog onClose={() => setAverages(false)} />}
    </Box>
  );
}

function TargetDialog({ target, customers, onClose, onSaved }) {
  const [form, setForm] = useState({ customer_id: target.customer_id || '', environment: target.environment, counts_towards_sla: target.counts_towards_sla });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setError(null);
    try { await uptimeAPI.updateTarget(target.id, { customer_id: form.customer_id ? Number(form.customer_id) : null, environment: form.environment, counts_towards_sla: form.counts_towards_sla && form.environment === 'production' && !!form.customer_id }); onSaved(); }
    catch (err) { setError(errText(err, 'Could not save')); setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Uptime URL<Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{target.url}</Typography></DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select size="small" label="Customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} slotProps={NATIVE}>
          <option value="">Not a customer system</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
        </TextField>
        <TextField select size="small" label="Environment" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} slotProps={NATIVE}>
          {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
        </TextField>
        <FormControlLabel control={<Checkbox checked={form.counts_towards_sla && form.environment === 'production' && !!form.customer_id} disabled={form.environment !== 'production' || !form.customer_id}
          onChange={(e) => setForm({ ...form, counts_towards_sla: e.target.checked })} />} label="This is the production URL that counts towards the customer's uptime" />
        <Typography variant="caption" color="text.secondary">Only one production URL per customer counts. Untick the old one first to move it.</Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={save} disabled={busy}>Save</Button></DialogActions>
    </Dialog>
  );
}

function Targets({ isAdmin }) {
  const [targets, setTargets] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState('attention');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    try { setTargets((await uptimeAPI.targets()).data); setError(null); } catch (err) { setError(errText(err, 'Could not load the URLs')); }
  }, []);
  useEffect(() => { load(); serverResourcesAPI.customers().then((r) => setCustomers(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { setPage(0); }, [view, search]);

  const counted = useMemo(() => new Set((targets || []).filter((t) => t.counts_towards_sla).map((t) => t.customer_id)), [targets]);   // URLs of inactive customers are left out of the working views
  const needs = (t) => t.is_active && t.customer_active !== false && t.environment === 'production' && !t.counts_towards_sla && (!t.customer_id || !counted.has(t.customer_id));
  const shown = (targets || []).filter((t) => (view === 'all' || (t.customer_active !== false && (view === 'attention' ? needs(t) : view === 'counted' ? t.counts_towards_sla : t.environment === 'production')))
    && (!search.trim() || `${t.url} ${t.cust_code || ''} ${t.cust_name || ''}`.toLowerCase().includes(search.trim().toLowerCase())));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The monitoring servers probe these URLs. The production URL marked as counting is the one a customer's uptime is measured on. New URLs are matched to customers automatically from their names; check the ones that need a decision.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Show" value={view} onChange={(e) => setView(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 240 }}>
          <option value="attention">Production URLs needing a decision</option>
          <option value="counted">URLs counting towards uptime</option>
          <option value="production">All production URLs</option>
          <option value="all">Every URL (including inactive customers)</option>
        </TextField>
        <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="URL or customer" sx={{ minWidth: 260 }} />
      </Paper>
      {targets && shown.length === 0 && <Alert severity="info">Nothing to show here.</Alert>}
      {shown.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>URL</TableCell><TableCell>Customer</TableCell><TableCell>Environment</TableCell><TableCell>Counts</TableCell><TableCell>Now</TableCell>{isAdmin && <TableCell />}</TableRow></TableHead>
              <TableBody>
                {shown.slice(page * PER, page * PER + PER).map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: 520 }}>{t.url}</TableCell>
                    <TableCell>{t.cust_code ? `${t.cust_code} - ${t.cust_name}` : <Typography variant="body2" color="text.secondary">not linked</Typography>}</TableCell>
                    <TableCell>{t.environment}</TableCell>
                    <TableCell>{t.counts_towards_sla ? <Chip size="small" color="success" label="Counts" /> : (needs(t) ? <Chip size="small" color="warning" label="Needs a decision" /> : '-')}</TableCell>
                    <TableCell>{t.last_state ? <Chip size="small" variant="outlined" color={t.last_state === 'up' ? 'success' : 'error'} label={t.last_state === 'up' ? 'Up' : 'Down'} /> : '-'}</TableCell>
                    {isAdmin && <TableCell><IconButton size="small" aria-label={`Edit ${t.url}`} onClick={() => setEdit(t)}><EditIcon fontSize="small" /></IconButton></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={shown.length} page={page} rowsPerPage={PER} rowsPerPageOptions={[PER]} onPageChange={(e, p) => setPage(p)} />
        </Paper>
      )}
      {edit && <TargetDialog target={edit} customers={customers} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Box>
  );
}

function Maintenance({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ customer_id: '', starts_at: '', ends_at: '', reason: '', ticket: '' });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await uptimeAPI.maintenance()).data); setError(null); } catch (err) { setError(errText(err, 'Could not load maintenance windows')); }
  }, []);
  useEffect(() => { load(); serverResourcesAPI.customers().then((r) => setCustomers(r.data)).catch(() => {}); }, [load]);

  const ready = form.starts_at && form.ends_at && form.reason.trim().length >= 5;
  const add = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await uptimeAPI.addMaintenance({ customer_id: form.customer_id ? Number(form.customer_id) : null, starts_at: form.starts_at, ends_at: form.ends_at, reason: form.reason.trim(), ticket: form.ticket.trim() || null });
      setForm({ customer_id: '', starts_at: '', ends_at: '', reason: '', ticket: '' }); setMessage('Maintenance window added. Downtime inside it is no longer counted.'); load();
    } catch (err) { setError(errText(err, 'Could not add the window')); }
    setBusy(false);
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this maintenance window? The time inside it will count as downtime again.')) return;
    try { await uptimeAPI.removeMaintenance(id); load(); } catch (err) { setError(errText(err, 'Could not remove the window')); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Planned maintenance is not counted as downtime. A window can cover one customer or everyone. All times are UTC. Windows are never deleted: removing one records who removed it.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {isAdmin && (
        <Paper sx={{ p: 2, mb: 2, display: 'grid', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Add a maintenance window</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} slotProps={NATIVE} sx={{ minWidth: 260 }}>
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
            </TextField>
            <TextField size="small" type="datetime-local" label="Starts (UTC)" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" type="datetime-local" label="Ends (UTC)" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" label="Ticket (optional)" value={form.ticket} onChange={(e) => setForm({ ...form, ticket: e.target.value })} />
          </Box>
          <TextField size="small" label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth />
          <Box><Button variant="contained" onClick={add} disabled={busy || !ready}>Add window</Button></Box>
        </Paper>
      )}
      {rows && rows.length === 0 && <Alert severity="info">No maintenance windows have been recorded.</Alert>}
      {rows && rows.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Starts</TableCell><TableCell>Ends</TableCell><TableCell>Customer</TableCell><TableCell>Reason</TableCell><TableCell>Ticket</TableCell><TableCell>Added by</TableCell>{isAdmin && <TableCell />}</TableRow></TableHead>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{fmtUtc(m.starts_at)}</TableCell><TableCell>{fmtUtc(m.ends_at)}</TableCell>
                    <TableCell>{m.cust_code ? `${m.cust_code} - ${m.cust_name}` : 'All customers'}</TableCell>
                    <TableCell>{m.reason}</TableCell><TableCell>{m.ticket || '-'}</TableCell><TableCell>{m.created_by}</TableCell>
                    {isAdmin && <TableCell><IconButton size="small" aria-label={`Remove window ${m.id}`} onClick={() => remove(m.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

function Uptime() {
  const { isAdmin } = usePermissions();
  const admin = isAdmin();
  const [tab, setTab] = useState(0);
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(() => uptimeAPI.status().then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const refresh = async () => {
    setRefreshing(true); setError(null);
    try { await uptimeAPI.sync(2); await loadStatus(); }
    catch (err) { setError(errText(err, 'The refresh failed')); }
    setRefreshing(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4">Customer Uptime</Typography>
        {admin && <Button startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={refresh} disabled={refreshing}>Refresh from Sumo</Button>}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Commitment: 99.9% uptime each month (about 43 minutes of downtime in a 30-day month). The monitoring servers in Oregon and London check each customer's production system every 2 minutes; a check slot counts as down only when both fail. Planned maintenance that an administrator has recorded is taken off.
      </Typography>
      {status && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {status.data_until ? `Stored up to ${fmtUtc(status.data_until)}` : 'Nothing has been pulled from Sumo yet'}
          {status.first_day ? `. History kept from ${status.first_day}.` : '.'}
          {status.last_run?.status === 'failed' ? ` The last refresh failed: ${status.last_run.error}` : ''}
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Monthly uptime" /><Tab label="URLs" /><Tab label="Maintenance" />
      </Tabs>
      {tab === 0 && <Monthly status={status?.data_until} />}
      {tab === 1 && <Targets isAdmin={admin} />}
      {tab === 2 && <Maintenance isAdmin={admin} />}
    </Box>
  );
}

export default Uptime;
